import React from "react";
import { ShieldCheck, RefreshCw, Plus, Minus, ChevronRight, Lock } from "lucide-react";
import { SHOP } from "@/constants/testIds";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import WhatsAppIcon from "./WhatsAppIcon";
import SmartImage from "./SmartImage";
import { buildProductWhatsAppUrl, buildStockEnquiryUrl } from "@/lib/whatsapp";

export default function ProductCard({ product, onOpenDetails }) {
  const { items, addItem, updateQty } = useCart();
  const { user, openLogin } = useAuth();
  const variantCount = product.variant_count ?? (Array.isArray(product.variants) ? product.variants.length : 0);
  const hasVariants = variantCount > 0;
  const outOfStock = Number(product.stock) === 0;

  // For simple products only — quantity tracking direct on card.
  const cartItem = !hasVariants && items.find((i) => i.product_id === product.id);
  const qty = cartItem?.qty || 0;
  const discount = product.mrp && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;

  const optionGroupNames = hasVariants
    ? product.option_groups.map((g) => g.name).join(" · ")
    : "";

  return (
    <div
      data-testid={SHOP.productCard(product.id)}
      className="group relative flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300 transition-all duration-200"
    >
      {/* Image (clickable to open details) */}
      <button
        type="button"
        onClick={() => onOpenDetails?.(product)}
        className="relative aspect-square bg-gray-50 overflow-hidden block text-left"
        aria-label={`View ${product.name}`}
      >
        <SmartImage
          product={product}
          width={480}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {discount > 0 && (
          <div className="absolute top-2 left-2 bg-[#FF5722] text-white text-[10px] sm:text-xs font-extrabold px-2 py-1 rounded">
            {discount}% OFF
          </div>
        )}
        {product.qr_verified && (
          <div className="absolute top-2 right-2 bg-[#16A34A] text-white text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 shadow">
            <ShieldCheck size={10} strokeWidth={3} />
            QR Verified
          </div>
        )}
        {hasVariants && (
          <div className="absolute bottom-2 left-2 bg-gray-900/85 text-white text-[10px] font-bold px-2 py-0.5 rounded backdrop-blur">
            {variantCount} options
          </div>
        )}
      </button>

      {/* Body */}
      <div className="flex-1 flex flex-col p-3 sm:p-4">
        <div className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#0056D2]">
          {product.brand}
        </div>
        <button
          type="button"
          onClick={() => onOpenDetails?.(product)}
          className="mt-1 text-xs sm:text-sm font-semibold text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem] text-left hover:underline"
        >
          {product.name}
        </button>

        {/* Price */}
        {user ? (
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            {hasVariants && (
              <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">From</span>
            )}
            <span className="text-base sm:text-lg font-extrabold text-gray-900">
              ₹{product.price.toLocaleString("en-IN")}
            </span>
            {product.mrp > product.price && (
              <span className="text-xs text-gray-500 line-through">
                ₹{product.mrp.toLocaleString("en-IN")}
              </span>
            )}
          </div>
        ) : (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#0056D2]">
            <Lock size={12} /> Login to view price
          </div>
        )}

        {/* Variant axes summary */}
        {hasVariants ? (
          <div className="mt-1 text-[10px] sm:text-[11px] font-semibold text-gray-600 line-clamp-1">
            {optionGroupNames}
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {user && product.bulk_unlock_price && (
              <span className="bg-[#FFD700] text-gray-900 text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded font-extrabold uppercase tracking-wider">
                Bulk ₹{product.bulk_unlock_price}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] text-gray-600 font-semibold">
              <RefreshCw size={10} strokeWidth={2.5} />
              Toll free home service
            </span>
          </div>
        )}

        {/* Add / Select Options */}
        <div className="mt-3 sm:mt-4 flex items-stretch gap-2">
          {outOfStock ? (
            <>
              <div
                data-testid={`out-of-stock-${product.id}`}
                className="flex-1 h-9 sm:h-10 rounded-md bg-gray-200 text-gray-600 text-[10px] sm:text-xs font-extrabold uppercase tracking-wide inline-flex items-center justify-center text-center px-1"
              >
                No Stock · Coming Soon
              </div>
              <a
                data-testid={`stock-enquiry-${product.id}`}
                href={buildStockEnquiryUrl(product)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Ask about ${product.name} stock on WhatsApp`}
                title="WhatsApp us for stock information"
                className="shrink-0 w-9 sm:w-10 h-9 sm:h-10 rounded-md bg-[#25D366] hover:bg-[#1FB855] active:scale-95 text-white grid place-items-center transition-all shadow-sm"
              >
                <WhatsAppIcon size={18} />
              </a>
            </>
          ) : !user ? (
            <button
              data-testid={`login-to-order-${product.id}`}
              onClick={openLogin}
              className="flex-1 h-9 sm:h-10 rounded-md bg-[#0056D2] hover:bg-[#0043A6] active:scale-[0.98] text-white text-xs sm:text-sm font-extrabold uppercase tracking-wide transition-all inline-flex items-center justify-center gap-1.5"
            >
              <Lock size={13} /> Login to order
            </button>
          ) : hasVariants ? (
            <button
              data-testid={SHOP.addToCartBtn(product.id)}
              onClick={() => onOpenDetails?.(product)}
              className="flex-1 h-9 sm:h-10 rounded-md bg-[#FF5722] hover:bg-[#E64A19] active:scale-[0.98] text-white text-xs sm:text-sm font-extrabold uppercase tracking-wide transition-all inline-flex items-center justify-center gap-1"
            >
              Select Options <ChevronRight size={14} strokeWidth={3} />
            </button>
          ) : qty === 0 ? (
            <button
              data-testid={SHOP.addToCartBtn(product.id)}
              onClick={() => addItem(product, 1)}
              className="flex-1 h-9 sm:h-10 rounded-md bg-[#FF5722] hover:bg-[#E64A19] active:scale-[0.98] text-white text-xs sm:text-sm font-extrabold uppercase tracking-wide transition-all"
            >
              Add
            </button>
          ) : (
            <div className="flex-1 flex items-center justify-between h-9 sm:h-10 rounded-md bg-[#FF5722] text-white">
              <button
                data-testid={SHOP.qtyDecrease(product.id)}
                onClick={() => updateQty(product.id, qty - 1)}
                className="h-full w-10 grid place-items-center hover:bg-[#E64A19] rounded-l-md active:scale-95 transition-transform"
              >
                <Minus size={16} strokeWidth={3} />
              </button>
              <span className="font-extrabold text-sm">{qty}</span>
              <button
                data-testid={SHOP.qtyIncrease(product.id)}
                onClick={() => updateQty(product.id, qty + 1)}
                className="h-full w-10 grid place-items-center hover:bg-[#E64A19] rounded-r-md active:scale-95 transition-transform"
              >
                <Plus size={16} strokeWidth={3} />
              </button>
            </div>
          )}
          {user && !outOfStock && (
            <a
              data-testid={SHOP.productWhatsappBtn(product.id)}
              href={buildProductWhatsAppUrl(product, Math.max(qty, 1))}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Order ${product.name} on WhatsApp`}
              title="Order on WhatsApp"
              className="shrink-0 w-9 sm:w-10 h-9 sm:h-10 rounded-md bg-[#25D366] hover:bg-[#1FB855] active:scale-95 text-white grid place-items-center transition-all shadow-sm"
            >
              <WhatsAppIcon size={18} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
